/**
 * Busca no guia — por TERMOS, em qualquer ordem, sem acento.
 *
 * `includes(consulta)` inteira quebra com a ordem trocada: "entrada custo" não acharia
 * "Custo por entrada". Aqui cada palavra digitada precisa aparecer em algum lugar do passo
 * (título, texto, itens, blocos, notas), e o título pesa mais na ordenação — quem procura
 * "evasão" quer primeiro o passo que SE CHAMA evasão, não o que a cita de passagem.
 */
import { normalizeSearch } from '@mgdlead/ui';

import type { CapituloDoGuia, PassoDoGuia, SecaoDoGuia } from './tipos';

export interface ResultadoDaBusca {
  capitulo: CapituloDoGuia;
  secao: SecaoDoGuia;
  passo: PassoDoGuia;
  /** Um pedaço do texto em volta do primeiro termo, para a pessoa reconhecer o trecho. */
  trecho: string;
  pontos: number;
}

function textoDoPasso(passo: PassoDoGuia): string {
  return [
    passo.texto,
    passo.onde ?? '',
    ...(passo.itens ?? []),
    ...(passo.blocos ?? []).map((b) => `${b.rotulo} ${b.texto}`),
    ...(passo.notas ?? []).map((n) => `${n.titulo} ${n.texto}`),
    passo.imagem?.marcadores?.map((m) => m.rotulo).join(' ') ?? '',
  ].join(' ');
}

function trechoEmVolta(texto: string, termo: string): string {
  const plano = texto.replace(/\s+/g, ' ').trim();
  const idx = normalizeSearch(plano).indexOf(termo);
  if (idx < 0) return plano.slice(0, 140) + (plano.length > 140 ? '…' : '');
  const inicio = Math.max(0, idx - 50);
  const fim = Math.min(plano.length, idx + 110);
  return `${inicio > 0 ? '…' : ''}${plano.slice(inicio, fim)}${fim < plano.length ? '…' : ''}`;
}

export function buscarNoGuia(
  capitulos: readonly CapituloDoGuia[],
  consulta: string,
  limite = 30,
): ResultadoDaBusca[] {
  const termos = normalizeSearch(consulta)
    .split(' ')
    .filter((t) => t.length > 0);
  if (termos.length === 0) return [];

  const resultados: ResultadoDaBusca[] = [];
  for (const capitulo of capitulos) {
    for (const secao of capitulo.secoes) {
      for (const passo of secao.passos) {
        const titulo = normalizeSearch(passo.titulo);
        const corpo = normalizeSearch(textoDoPasso(passo));
        const contexto = normalizeSearch(`${capitulo.titulo} ${secao.titulo}`);
        let pontos = 0;
        let todos = true;
        for (const termo of termos) {
          if (titulo.includes(termo)) pontos += 5;
          else if (corpo.includes(termo)) pontos += 2;
          else if (contexto.includes(termo)) pontos += 1;
          else {
            todos = false;
            break;
          }
        }
        if (!todos) continue;
        resultados.push({
          capitulo,
          secao,
          passo,
          trecho: trechoEmVolta(passo.texto || textoDoPasso(passo), termos[0] ?? ''),
          pontos,
        });
      }
    }
  }
  return resultados.sort((a, b) => b.pontos - a.pontos).slice(0, limite);
}

/** Minutos de leitura, a ~180 palavras por minuto (texto técnico, lido com a tela ao lado). */
export function minutosDeLeitura(capitulo: CapituloDoGuia): number {
  let palavras = 0;
  for (const secao of capitulo.secoes) {
    for (const passo of secao.passos) palavras += textoDoPasso(passo).split(/\s+/).length;
  }
  return Math.max(1, Math.round(palavras / 180));
}

export function contarPassos(capitulo: CapituloDoGuia): number {
  return capitulo.secoes.reduce((total, s) => total + s.passos.length, 0);
}
